import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    FafsaSubmissionRate,
    FafsaDashboardFilter,
    FafsaYearTotal,
    FafsaGrandTotal
} from '../types'; import ApiService from 'services/apiService';

interface FafsaDataContextType {
  data: FafsaSubmissionRate[];
  loading: boolean;
  error: string | null;
  filter: FafsaDashboardFilter;
  activeSchoolYear: number | null;
  totalsByYear: FafsaYearTotal[];
  grandTotal: FafsaGrandTotal | null
  updateFilter: (filter: Partial<FafsaDashboardFilter>) => void;
  refetch: () => void;
}

const FafsaDataContext = createContext<FafsaDataContextType | undefined>(undefined);

export const FafsaDataProvider = ({ children }: { children: React.ReactNode }) => {
  const [data, setData] = useState<FafsaSubmissionRate[]>([]);
  const [totalsByYear, setTotalsByYear] = useState<FafsaYearTotal[]>([]);
  const [grandTotal, setGrandTotal] = useState<FafsaGrandTotal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
  const [filter, setFilter] = useState<FafsaDashboardFilter>({
    schoolYears: [],
    groupBySchool: false,
  });
  const [initialized, setInitialized] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.instance.post(
        '/FafsaDashboard/GetFafsaSubmissionRates',
        filter
      );
      
      const responseData = typeof response.data === 'string' 
        ? JSON.parse(response.data) 
        : response.data;
      
      if (responseData.activeSchoolYear && !activeSchoolYear) {
        setActiveSchoolYear(responseData.activeSchoolYear);
        
        if (!initialized && filter.schoolYears.length === 0) {
          setFilter(prev => ({
            ...prev,
            schoolYears: [responseData.activeSchoolYear]
          }));
          setInitialized(true);
          return;
        }
      }
      
      setData(responseData.data || []);
      setTotalsByYear(responseData.totalsByYear || []);
      setGrandTotal(responseData.grandTotal || null);
    } catch (err) {
      setError('Failed to load FAFSA data');
      console.error('Error fetching FAFSA data:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (initialized) {
      fetchData();
    }
  }, [filter]);

  const updateFilter = (updates: Partial<FafsaDashboardFilter>) => {
    setFilter(prev => ({ ...prev, ...updates }));
  };

  const refetch = () => {
    fetchData();
  };

  return (
    <FafsaDataContext.Provider
      value={{
        data,
        loading,
        error,
        filter,
        activeSchoolYear,
        totalsByYear,
        grandTotal,
        updateFilter,
        refetch,
      }}
    >
      {children}
    </FafsaDataContext.Provider>
  );
};

export const useFafsaData = () => {
  const context = useContext(FafsaDataContext);
  if (!context) {
    throw new Error('useFafsaData must be used within FafsaDataProvider');
  }
  return context;
};